// Shared treatment for the title overlaid on a video thumbnail.
//
// Explore, Workspace and the home row all draw the same label on the same artwork, so the
// font, the LaTeX handling and the two-line clamp live here rather than in three pages that
// slowly diverge — which is exactly what had happened: Workspace rendered the raw string, so
// a title containing math showed its literal `$...$`, in a different typeface to Explore.
import { renderLatex } from "../study/renderLatex";

export const ROW_TITLE_FONT = "'Nunito', system-ui, -apple-system, sans-serif";

// Strip newlines and cap the length before any math is rendered — a prompt pasted as a title
// can be several paragraphs, and KaTeX on all of it is wasted work behind a 2-line clamp.
/* The lesson title as plain type above the player.
 *
 * No plate, no pill, no outline — the title is a heading and is set like one. It reads as
 * page furniture rather than as part of the player, which is the trade: the joined bar makes
 * title-and-video one object, this makes the video the only object on the page.
 *
 * Cheapest of the treatments in height, which matters here more than it looks: the player is
 * capped by available height and locked to 16:9, so every vertical pixel above it costs 1.78
 * of picture width. Plain 26px type plus its margin is 38px; the bar was 42 and the padded
 * pill 56.
 */
export const TITLE_HEADING_CLASS =
  "leading-tight font-black tracking-tight text-slate-900 [&_.katex]:text-[0.9em]";

/* Size FLUIDLY, and larger than it first looks like it should be.
 *
 * The first pass set a flat 26px and it read as small — not because anything was broken (it
 * measured 26px at weight 900 with Nunito's 900 face genuinely loaded) but because 26px is
 * 2.5% of a 1056px-wide player. The treatments it replaced got away with that size by having
 * a filled plate behind them; plain type has no mass of its own, so it has to be bigger to
 * carry the same weight against a large black rectangle.
 *
 * vw rather than a second fixed step, because the player is sized from the viewport too — a
 * fixed heading drifts smaller the wider the window gets. The 20px floor is the phone size
 * and the 34px ceiling stops it competing with the lesson on very wide screens.
 */
export const titleHeadingStyle = () => ({
  fontFamily: ROW_TITLE_FONT,
  fontSize: "clamp(20px, 2.1vw, 34px)",
  lineHeight: 1.15,
});

export const cleanTitle = (text) => {
  if (!text) return "Untitled";
  const firstLine = text.split("\n")[0].trim();
  return firstLine.length > 120 ? firstLine.substring(0, 120) + "..." : firstLine;
};

export const safeRenderLatex = (text) => {
  const clean = cleanTitle(text);
  try {
    return renderLatex(clean);
  } catch {
    return clean.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
};

// Two lines, then an ellipsis.
//
// This MUST sit on an element with no padding of its own. `-webkit-line-clamp` clips at the
// content box, so bottom padding on the clamped element is a gap the third line paints into
// instead of being hidden — which is why Workspace's `p-3` cards showed a half-height third
// line sliced through the middle of the glyphs. Put the padding on a wrapper.
/* Two lines, then an ellipsis — and a HARD CEILING as well as the clamp.
 *
 * -webkit-line-clamp alone is not enough. It needs `display: -webkit-box`, and engines do not
 * agree about that box: Chromium normalises the computed value to `flow-root` (even when the
 * declaration carries !important), and titles that clamp to two lines there were rendering
 * three on iOS Safari, where a KaTeX fragment on a line changes its height. The clamp is what
 * produces the "…", so it stays; max-height is what guarantees the promise.
 *
 * line-height is pinned here rather than left to a `leading-*` class, because a ceiling
 * expressed in em is only correct if it knows the line height it is multiplying.
 */
export const CLAMP_2_LINES = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
  lineHeight: 1.35,
  maxHeight: "2.7em",
};

// The scrim under the title. Dark enough at the baseline to keep white text legible on a
// bright frame, gone by ~70% up so it never reads as a bar across the artwork.
export const TITLE_SCRIM =
  "linear-gradient(to top, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.35) 45%, transparent 72%)";
